import React, { FC, useState } from 'react';
import { TransactionError } from '@thirdweb-dev/react';
import { Box, Button, HStack, Text } from '@chakra-ui/react';
import { NetworkMismatchButton } from '@app/components/NetworkMismatchButton';
import { SubmitButton } from '@app/components/SubmitButton';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import { DebouncedInput } from '@app/components/DebouncedInput';
import { TransactionResultModal } from '@app/components/TransactionResultModal';
import { useDeallocate } from '@app/hooks/ryo/useDeallocate';
import { LabeledValue } from '@app/components/LabeledValue';
import { kFormatter2 } from '@app/helpers/format';
import { useNetworkMismatch } from '@app/hooks/thirdweb/useNetworkMismatch';

interface Props {
  onClose: () => void;
}

export const Deallocate: FC<Props> = ({ onClose }) => {
  const [feedback, setFeedback] = useState<{
    status: 'success' | 'error';
    title: string;
    message: string;
    txHash?: string;
  } | null>(null);
  const {
    state,
    handleAmountChange,
    deallocate,
    deallocating,
    resetStateValues,
    usageAllocation,
    isLoading
  } = useDeallocate();
  const isMismatchedNetwork = useNetworkMismatch();
  // const { userAllocation, totalAllocation, dividendsShare, isLoading } =
  //   useDividentsStats();

  const isBalanceExceeded =
    usageAllocation !== null && +usageAllocation < +state.amount;

  function getSubmitLabel() {
    if (isBalanceExceeded) {
      return 'Insufficient balance';
    }

    if (!state.amount) {
      return 'Enter xKIM amount to deallocate';
    }

    return 'Deallocate';
  }

  function renderSubmitAction() {
    if (isMismatchedNetwork) {
      return <NetworkMismatchButton />;
    }

    return (
      <SubmitButton
        disabled={isBalanceExceeded || !+state.amount}
        mt="40px"
        onClick={async () => {
          try {
            const res = await deallocate();

            if (res['reason'] !== undefined) {
              showErrorToast(res.reason);
            } else if (res instanceof Error) {
              showErrorToast(res.message);
            } else {
              resetStateValues();

              const txHash = res?.receipt?.transactionHash;

              showSuccessToast(`Success`, <ExternalLink txHash={txHash} />);

              setFeedback({
                status: 'success',
                title: 'Transaction Confirmed',
                message: 'Your transaction has been successfully completed',
                txHash
              });
            }
          } catch (e) {
            if (e instanceof TransactionError) {
              showErrorToast(e.reason);
            } else {
              showErrorToast(`Error`);
            }

            setFeedback({
              status: 'error',
              title: 'Transaction Error',
              message:
                'There was an error processing your transaction. Please check the details and try again'
            });
          }
        }}
        isLoading={deallocating}
        label={getSubmitLabel()}
      />
    );
  }

  return (
    <Box mt="52px">
      <HStack justifyContent="space-between" mt="18px">
        <Text fontSize="16px" fontWeight={400} lineHeight="26px">
          Enter xKIM amount
        </Text>

        <LabeledValue
          label="Allocated xKIM:"
          isLoading={isLoading}
          value={kFormatter2(Number(usageAllocation))}
        />
      </HStack>
      <HStack
        justifyContent="space-between"
        mt="18px"
        sx={{
          paddingRight: '14px',
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'gray.5',
          background: 'black'
        }}
      >
        <DebouncedInput
          amount={state.amount}
          onChange={handleAmountChange}
          textAlign="left"
        />
        <Button
          variant="tertiary"
          onClick={() => {
            if (usageAllocation === null) {
              return;
            }

            handleAmountChange(usageAllocation.toString());
          }}
          sx={{
            color: 'orange',
            bg: 'gray.2',
            width: 'fit-content',
            fontSize: '14px',
            padding: '8px 14px'
          }}
        >
          MAX
        </Button>
      </HStack>
      {renderSubmitAction()}
      <TransactionResultModal
        isOpen={!!feedback}
        status={feedback?.status}
        title={feedback?.title}
        message={feedback?.message}
        txHash={feedback?.txHash}
        onClose={() => {
          setFeedback(null);

          onClose();
        }}
      />
    </Box>
  );
};
